<template>
  <div>
    <vx-card
      class="mt-base"
      title="Environmental Science Activity 4:  What Is My Ecological Footprint?"
    >
      <h4 class="mb-5">Task 1: Calculate Your Ecological Footprint:</h4>

      <p class="mb-5">
        An ecological footprint can be defined as the impact of a person’s activities measured in
        terms of land and water area required to produce the goods consumed and absorb the wastes
        generated.
      </p>
      <p class="mb-5">
        Put simply, it is the amount of the environment required to produce the goods and services
        necessary to support a particular lifestyle.
      </p>
      <p class="mb-5">
        To calculate your own ecological footprint, use the Ecological Footprint Calculator tool
        below (there’s a short video to show you what to do as well).
      </p>

      <vs-row vs-align="center" vs-w="12">
        <vs-col vs-lg="6" vs-sm="6" vs-xs="12">
          <a href="https://www.footprintcalculator.org/" target="_blank">
            <img
              :src="image1"
              alt="Open the Ecological Footprint Calculator (opens in a new tab)"
              class="img-responsive mx-auto cursor-pointer mb-5"
            />
          </a>
        </vs-col>
        <vs-col vs-lg="6" vs-sm="6" vs-xs="12">
          <button
            type="button"
            class="no-style img-click-button"
            aria-label="Play video: How to use the Ecological Footprint Calculator"
            @click="showVideoPopup(videos[1].video)"
          >
            <img :src="videos[1].image" class="img-responsive mx-auto cursor-pointer mb-5" alt="" />
          </button>
        </vs-col>
      </vs-row>

      <p class="mb-5">
        When you have finished the quiz, take a look over your results and answer the following
        questions:
      </p>
      <p class="mb-5 text-bold">If everyone lived like you, how many Earths would we need?</p>

      <div class="overflow-hidden">
        <text-block
          v-model="data['question1']"
          :editable="isEditable"
          style="width: 100px; float: left"
          pclass="my-2"
        />
        <p class="float-left py-2 pl-2"><strong>Earths</strong></p>
      </div>
      <p class="mb-5 text-bold">What is your Ecological Footprint number?</p>
      <div class="overflow-hidden">
        <text-block
          v-model="data['question2']"
          :editable="isEditable"
          style="width: 100px; float: left"
          pclass="my-2"
        />
        <p class="float-left py-2 pl-2"><strong>global hectares</strong></p>
      </div>

      <p class="mb-5">
        If you want to take more time to compare your ecological footprint to the average ecological
        footprint in Australia and other countries around the world, you can look further at the
        “Open Data Platform” shown at the end of the above activity (use the button below).
      </p>
      <a href="https://data.footprintnetwork.org/" target="_blank">
        <centered-image-wrapper
          alt="Open the Global Footprint Network data platform (opens in a new tab)"
          :image="require('@/assets/images/experiences/envscience/open_data_button.png')"
        />
      </a>

      <h4 class="mb-5">Task 2: PlantNet: A Citizen Science Project on Biodiversity</h4>
      <p class="mb-5">
        To work in the field of environmental science, you’ll need to get pretty good at recognising
        and identifying plants and animals. There’s no better place to start practising than in your
        own backyard - or front!
      </p>
      <p class="mb-5">
        Luckily, there are technological tools available to help you upgrade your knowledge of the
        natural world. “Seek” uses powerful image recognition technology to help identify plants and
        animals.
      </p>

      <vs-row vs-align="center" vs-w="12">
        <vs-col vs-lg="6" vs-sm="6" vs-xs="12">
          <p class="mb-5">
            Using identification apps such as Seek can be a fun way to improve your knowledge of the
            world around you. To get started, download it to your smartphone by visiting your
            preferred App. Store (Apple App Store or Google Play). Search for “Seek by iNaturalist”
            and install this App.
          </p>
        </vs-col>
        <vs-col vs-lg="6" vs-sm="6" vs-xs="12">
          <img :src="image2" class="img-responsive mx-auto cursor-pointer mb-5" alt="" />
        </vs-col>
      </vs-row>

      <vs-row vs-align="center" vs-w="12">
        <vs-col vs-lg="6" vs-sm="6" vs-xs="12">
          <p class="mb-5">To use Seek:</p>

          <ul class="default-list">
            <li>Go outside, open the app, and point your camera at living things</li>
            <li>
              Identify wildlife, plants, and fungi and learn about the organisms all around you
            </li>
            <li>
              Earn badges for observing different types of species and participating in challenges
              (optional)
            </li>
          </ul>
          <p class="mb-5">
            To find out more about all the features this app offers, you can download and read the
            PDF version of the user guide here (optional):
          </p>
        </vs-col>
        <vs-col vs-lg="6" vs-sm="6" vs-xs="12">
          <a :href="'content/envscience/seek_user_guide.pdf' | blob_url" target="_blank">
            <img
              :src="image3"
              alt="Open the Seek app user guide (PDF, opens in a new tab)"
              class="img-responsive mx-auto cursor-pointer mb-5"
            />
          </a>
        </vs-col>
      </vs-row>

      <p class="mb-5">
        Once you’ve got a good handle on using the app, it’s time to put it to good use.
      </p>
      <p class="mb-5">
        Explore the world outdoors and identify three plant species and two animal species. Trees,
        flowers, fungi, etc., can all count toward your plant total. While mammals, fish, reptiles,
        insects, birds, etc., can all count toward your animal total.
      </p>
      <p class="mb-5">
        Please note, though, the focus here should be on wild plants and animals, so forget about
        those geraniums in your grandparents’ garden or the neighbour’s pet dog!
      </p>
      <p class="mb-5">
        You can look for these wild species in your garden, local park, or even in the bush - if you
        have access and take the correct safety precautions! You can even go urban hiking around
        town and uncover stubborn plants and creatures, making their home amid the hustle and bustle
        of human activity.
      </p>
      <p class="mb-5">
        You can record your newly identified species’ names in the table below and upload an
        accompanying photo too.
      </p>

      <div v-for="index in 3" :key="'1_' + index" class="mb-5">
        <p class="mb-5 text-bold">Plant Species {{ index }}</p>
        <div class="overflow-hidden">
          <p class="float-left py-2 pr-2">Name:</p>
          <text-block
            v-model="data['question' + (index + 2) + '_1']"
            :editable="isEditable"
            style="width: 380px; float: left"
            pclass="my-2"
          />
        </div>
        <div class="overflow-hidden">
          <p class="float-left py-2 pr-2">Where I found it:</p>
          <textarea-block
            v-model="data['question' + (index + 2) + '_2']"
            :editable="isEditable"
            style="width: 300px; float: left"
            pclass="my-2"
          />
        </div>
        <p class="mb-3">Upload Photo:</p>
        <fileupload-button-immediate
          v-model="uploaded_files['uploaded_file_' + index]"
          :is-editable="isEditable"
          filetype="photo"
          :index="index"
        />
      </div>
      <div v-for="index in 2" :key="'2_' + index" class="mb-5">
        <p class="mb-5 text-bold">Animal Species {{ index }}</p>
        <div class="overflow-hidden">
          <p class="float-left py-2 pr-2">Name:</p>
          <text-block
            v-model="data['question' + (index + 5) + '_1']"
            :editable="isEditable"
            style="width: 380px; float: left"
            pclass="my-2"
          />
        </div>
        <div class="overflow-hidden">
          <p class="float-left py-2 pr-2">Where I found it:</p>
          <textarea-block
            v-model="data['question' + (index + 5) + '_2']"
            :editable="isEditable"
            style="width: 300px; float: left"
            pclass="my-2"
          />
        </div>
        <p class="mb-3">Upload Photo:</p>
        <fileupload-button-immediate
          v-model="uploaded_files['uploaded_file_' + (3 + index)]"
          :is-editable="isEditable"
          filetype="photo"
          :index="3 + index"
        />
      </div>
      <save-buttons :is-editable="isEditable" @save="save" />
    </vx-card>
  </div>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/envscience/ecological_footprint_help_trigger.jpg'),
          video:
            $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
            '/content/envscience/ecological_footprint_1000.mp4',
        },
      },
      image1: require('@/assets/images/experiences/envscience/ecological_footprint_button.png'),
      image2: require('@/assets/images/experiences/envscience/app_store_seek.jpg'),
      image3: require('@/assets/images/experiences/envscience/seek_manual_button.png'),
      uploaded_files: {
        uploaded_file_1: '',
        uploaded_file_2: '',
        uploaded_file_3: '',
        uploaded_file_4: '',
        uploaded_file_5: '',
        uploaded_file_6: '',
      },
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
  mounted() {
    if (typeof this.uploaded_files !== 'undefined') {
      for (let i = 1; i <= Object.keys(this.uploaded_files).length; i++) {
        if (typeof this.data['uploaded_file_' + i] !== 'undefined') {
          this.uploaded_files['uploaded_file_' + i] = this.data['uploaded_file_' + i];
        }
      }
    }
  },
};
</script>
